/**
 * Pure logic for the workspace switcher's progressive-disclosure search.
 *
 * Kept as framework-free functions (no Angular, no DOM) so they can be unit
 * tested directly under bare vitest with full branch coverage — mirroring the
 * repo's "pure logic in .ts, co-located pure spec" convention.
 *
 * SHARED, and that is the whole reason it is a file rather than three
 * methods. The switcher moved out of the shell bar and into the profile
 * panel's "Switch workspace" row (`41-profile-panel`), and a move that
 * reimplements the threshold and the match rule at the destination is a
 * rewrite wearing a move's name — two lists that answer "does this
 * organisation match?" differently is the defect, not the duplication.
 */

/** Minimal shape the filter needs — anything with a display `name`. */
export interface NamedCustomer {
  name: string;
}

/**
 * Above this many accessible organisations the switcher swaps its plain flat
 * list for a name-filter search box (progressive disclosure).
 *
 * It lives HERE, beside the predicate that reads it, so the surface that
 * renders the list and the function that decides whether to offer a search
 * box cannot drift apart. `org-switcher.component.ts` re-exports it for its
 * own spec.
 */
export const SWITCHER_SEARCH_THRESHOLD = 5;

/**
 * Whether the switcher should render its name-filter search box. The plain flat
 * list stays until the accessible-customer count exceeds `threshold`.
 */
export function shouldShowSearch(count: number, threshold: number): boolean {
  return count > threshold;
}

/**
 * Filter `list` by a case-insensitive substring match on each customer's name.
 * An empty / whitespace-only query returns the full list unchanged.
 */
export function filterCustomers<T extends NamedCustomer>(
  list: readonly T[],
  query: string,
): T[] {
  const q = query.trim().toLowerCase();
  if (!q) return list.slice();
  return list.filter((c) => c.name.toLowerCase().includes(q));
}

// ── The dialog form ───────────────────────────────────────────────────────

/**
 * Past this many workspaces the profile panel's "Switch workspace" row opens
 * a DIALOG instead of expanding in place. An inline list inside a side panel
 * is fine for a handful of customers; RDx support staff with all-customers
 * access reach every customer in the region, and a panel that scrolls a few
 * hundred rows is not a picker.
 */
export const SWITCHER_DIALOG_THRESHOLD = 8;

/** The dialog renders at most this many rows; the rest are found by typing. */
export const SWITCHER_DIALOG_RENDER_LIMIT = 100;

export function shouldUseDialog(count: number): boolean {
  return count > SWITCHER_DIALOG_THRESHOLD;
}

export interface RankedCustomers<T> {
  /** What to render: at most `limit` rows. */
  items: T[];
  /** How many matched before the cap — drives "showing N of M". */
  total: number;
}

/**
 * Filter and order for the dialog. Names that START with the query come
 * before names that merely contain it, then alphabetical — so typing "vo"
 * puts VodafoneThree above Novo. With no query the current workspace leads
 * and the rest are alphabetical. Case-insensitive, like `filterCustomers`.
 */
export function rankCustomers<T extends NamedCustomer & { id: string }>(
  list: readonly T[],
  query: string,
  currentId: string | null,
  limit: number = SWITCHER_DIALOG_RENDER_LIMIT,
): RankedCustomers<T> {
  const q = query.trim().toLowerCase();
  const byName = (a: T, b: T) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
  let ranked: T[];
  if (!q) {
    ranked = list.slice().sort((a, b) => {
      if (a.id === currentId) return -1;
      if (b.id === currentId) return 1;
      return byName(a, b);
    });
  } else {
    const starts: T[] = [];
    const contains: T[] = [];
    for (const c of list) {
      const name = c.name.toLowerCase();
      if (name.startsWith(q)) starts.push(c);
      else if (name.includes(q)) contains.push(c);
    }
    ranked = [...starts.sort(byName), ...contains.sort(byName)];
  }
  return { items: ranked.slice(0, Math.max(0, limit)), total: ranked.length };
}

/**
 * The next active row for an arrow key, wrapping at both ends. `-1` (no
 * active row yet) moves to the first row on Down and the last on Up; an
 * empty list has no active row.
 */
export function nextActiveIndex(
  current: number,
  delta: 1 | -1,
  length: number,
): number {
  if (length <= 0) return -1;
  if (current < 0) return delta === 1 ? 0 : length - 1;
  return (current + delta + length) % length;
}
